import { FC, useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { Box } from '@mui/material';

import { Text } from '@/components/Shared/Text';

import { useResizableColumns } from '../hooks/useResizableColumns';
import { UserUsageRow } from '../utils';

import { CellContextMenu } from './CellContextMenu';

import styles from '../styles.module.scss';

type SDKUsageByUserGridProps = {
  userUsage: UserUsageRow[];
};

const NoRowsOverlay = () => (
  <Box component="div" display="flex" alignItems="center" justifyContent="center" height="100%">
    <Text type="medium-12px">No token usage data found for the selected time period</Text>
  </Box>
);

const getUserColumns = (): GridColDef<UserUsageRow>[] => [
  {
    field: 'email',
    headerName: 'User',
    width: 260,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'totalUsage',
    headerName: 'Total Usage',
    width: 150,
    valueFormatter: (params) => `${(params.value as number).toFixed(2)} tokens`,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'simulationCount',
    headerName: 'Simulations',
    width: 120,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'projectCount',
    headerName: 'Projects',
    width: 120,
    headerClassName: styles['data-grid-header'],
  },
  {
    field: 'lastCompleted',
    headerName: 'Last Completed',
    width: 180,
    valueFormatter: (params) => (params.value != null ? dayjs(params.value as number).format('DD/MM/YYYY HH:mm') : '-'),
    headerClassName: styles['data-grid-header'],
  },
];

export const SDKUsageByUserGrid: FC<SDKUsageByUserGridProps> = ({ userUsage }) => {
  const userColumns = useMemo(() => getUserColumns(), []);
  const { columns, containerRef, containerNode } = useResizableColumns(userColumns);

  return (
    <div ref={containerRef} style={{ height: '100%' }}>
      <CellContextMenu container={containerNode} />
      <DataGrid
        rows={userUsage}
        columns={columns}
        getRowId={(row) => row.email}
        autoPageSize
        disableRowSelectionOnClick
        disableColumnSelector
        initialState={{
          sorting: { sortModel: [{ field: 'totalUsage', sort: 'desc' }] },
        }}
        slots={{ noRowsOverlay: NoRowsOverlay }}
        slotProps={{ row: { className: styles['data-grid-row'] } }}
        rowHeight={42}
        columnHeaderHeight={42}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnSeparator': {
            cursor: 'col-resize',
            '&:hover': { color: 'rgba(43, 143, 119, 0.8)' },
          },
        }}
      />
    </div>
  );
};
